import React, { useState, useContext } from 'react'
import { X } from 'lucide-react'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@edvi-tech/ui/dialog'
import Controls from '../Controls/Controls'
import { AuthContext } from '../../Context/AuthContext'
import axiosPost from '../../Global/Axios/axiosPost'
import handleError from '../../Global/HandleError/handleError'
import showSuccessSnackbar from '../Snackbar/successSnackbar'
import ConfirmDialog from './ConfirmDialog'

const DAY_OPTIONS = [
  { value: 0, label: 'Today' },
  { value: 1, label: 'Tomorrow' },
  { value: 2, label: 'Day After' },
]

const TIME_SLOTS = [
  { from: 9, to: 12 },
  { from: 12, to: 15 },
  { from: 15, to: 18 },
  { from: 18, to: 21 },
]

function toUtcString(date) {
  const pad = (n) => String(n).padStart(2, '0')

  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate(),
  )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds(),
  )}Z`
}

function getTimeRangeStrings(dayOffset, timeSlot) {
  const slot = TIME_SLOTS[timeSlot]
  if (!slot) return null

  const baseDate = new Date()
  baseDate.setDate(baseDate.getDate() + dayOffset)

  function buildUtcString(localHour) {
    const localDate = new Date(baseDate)
    localDate.setHours(localHour, 0, 0, 0)
    return toUtcString(localDate)
  }

  return {
    from: buildUtcString(slot.from),
    to: buildUtcString(slot.to),
  }
}

function isSlotInFutureToday(startHour) {
  const now = new Date()
  const slotStart = new Date()
  slotStart.setHours(startHour, 0, 0, 0)
  return slotStart.getTime() > now.getTime()
}

function formatSlotLabel(slot) {
  const fromHour = slot.from <= 12 ? slot.from : slot.from - 12
  const toHour = slot.to <= 12 ? slot.to : slot.to - 12
  const fromPeriod = slot.from < 12 ? 'AM' : 'PM'
  const toPeriod = slot.to < 12 ? 'AM' : 'PM'
  return `${fromHour}:00 ${fromPeriod} - ${toHour}:00 ${toPeriod}`
}

const RequestCallbackDialog = ({ open, close }) => {
  const { getAuthHeader } = useContext(AuthContext)
  const [dayOffset, setDayOffset] = useState(0)
  const [timeSlot, setTimeSlot] = useState(undefined)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const handleDayOffset = (offset) => {
    setDayOffset(offset)
    setTimeSlot(undefined)
  }

  const handleDialogClose = () => {
    close()
  }

  const visibleTimeSlots = TIME_SLOTS.filter((slot) => {
    if (dayOffset !== 0) return true
    return isSlotInFutureToday(slot.from)
  })

  const handleVerify = async () => {
    const result = getTimeRangeStrings(dayOffset, timeSlot)
    const serverData = {
      time_slots: [
        {
          from: result.from,
          to: result.to,
        },
      ],
    }
    try {
      const res = await axiosPost(`/user-callback-request/`, {
        data: serverData,
        headers: getAuthHeader(),
      })

      if (res.status === 201) {
        showSuccessSnackbar( 'Request Raised Successfully')
        close()
      }
    } catch (err) {
      handleError(err)
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleDialogClose()
        }}
      >
        <DialogContent
          className="edvi-dlg-content--sm gap-0 rounded-2xl border-[#eef0f6] p-0"
          showCloseButton={false}
        >
          <DialogClose asChild>
            <button
              type="button"
              className="edvi-dlg__close"
              aria-label="Close"
              onClick={handleDialogClose}
            >
              <X size={18} strokeWidth={2.25} />
            </button>
          </DialogClose>
          <DialogHeader className="space-y-1 px-6 pb-4 pt-6 text-center sm:text-center">
            <DialogTitle className="text-xl font-semibold text-[#1c2434]">
              Request Callback
            </DialogTitle>
          </DialogHeader>

          <div className="edvi-dlg__scroll edvi-callback-dlg px-6 pb-4">
            <p className="edvi-callback-dlg__hint">
              Please select a time to connect
            </p>

            <div
              className="edvi-callback-dlg__days"
              role="group"
              aria-label="day offset"
            >
              {DAY_OPTIONS.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  className={`edvi-callback-dlg__chip${
                    dayOffset === day.value
                      ? ' edvi-callback-dlg__chip--active'
                      : ''
                  }`}
                  aria-pressed={dayOffset === day.value}
                  onClick={() => handleDayOffset(day.value)}
                >
                  {day.label}
                </button>
              ))}
            </div>

            <div
              className="edvi-callback-dlg__slots"
              role="group"
              aria-label="slot offset"
            >
              {visibleTimeSlots.map((slot) => {
                const index = TIME_SLOTS.indexOf(slot)
                return (
                  <button
                    key={index}
                    type="button"
                    className={`edvi-callback-dlg__slot${
                      timeSlot === index
                        ? ' edvi-callback-dlg__slot--active'
                        : ''
                    }`}
                    aria-pressed={timeSlot === index}
                    onClick={() => setTimeSlot(index)}
                  >
                    {formatSlotLabel(slot)}
                  </button>
                )
              })}
            </div>
          </div>

          <DialogFooter className="edvi-dlg__actions justify-center border-t border-[#eef0f6] bg-[#fbfbfe] px-6 py-4">
            <Controls.Button
              text="Continue"
              onClick={() => setOpenConfirmDialog(true)}
              disabled={timeSlot === undefined}
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {openConfirmDialog ? (
        <ConfirmDialog
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          title="Confirm Request?"
          content="Are you sure you want to us to connect with you at the selected time?"
          yesAction={handleVerify}
          noAction={() => setOpenConfirmDialog(false)}
        />
      ) : null}
    </>
  )
}

export default RequestCallbackDialog
